import { Dialog, DialogConfig, DialogRef as CdkDialogRef } from "@angular/cdk/dialog";
import { ComponentType } from "@angular/cdk/overlay";
import { inject, Injectable, TemplateRef } from "@angular/core";
import { Observable } from "rxjs";

import { withMobileBottomSheetPanelClass } from "../helpers/mobile-bottom-sheet.helper";
import { normalizeMainDialogPanelClasses } from "../helpers/dialog-panel-class.helper";
import { closeCdkDialogWithAnimation, AzmDialogRef } from "./dialog-ref";
import { elevateToastOverlay, keepToastOverlayElevated } from "../../../../utilities/overlay/toast-overlay.helpers";

export { DIALOG_DATA } from "@angular/cdk/dialog";

@Injectable({ providedIn: "root" })
export class DialogService {
	private readonly dialog = inject(Dialog);

	get afterAllClosed(): Observable<void> {
		return this.dialog.afterAllClosed;
	}

	get openDialogs(): readonly CdkDialogRef<object, boolean | string | undefined>[] {
		return this.dialog.openDialogs;
	}

	open<T = object, D = object, R = boolean | string | undefined>(
		componentOrTemplate: ComponentType<T> | TemplateRef<T>,
		config: DialogConfig<D, CdkDialogRef<R, T>> = {},
	): AzmDialogRef<T, R> {
		let azmRef: AzmDialogRef<T, R> | undefined;

		const configProviders = config.providers;
		const panelClass = withMobileBottomSheetPanelClass(
			normalizeMainDialogPanelClasses(config.panelClass as string | string[] | undefined),
		);

		const cdkConfig: DialogConfig<D, CdkDialogRef<R, T>> = {
			backdropClass: "cdk-overlay-dark-backdrop",
			restoreFocus: config.restoreFocus ?? true,
			...config,
			panelClass,
			autoFocus: false,
			disableClose: true,
			providers: (dialogRef: CdkDialogRef<R, T>, config: DialogConfig<D, CdkDialogRef<R, T>>, container) => {
				azmRef = new AzmDialogRef<T, R>(dialogRef);
				const baseProviders = [{ provide: AzmDialogRef, useValue: azmRef }];

				if (!configProviders) {
					return baseProviders;
				}

				const extraProviders = typeof configProviders === "function" ? configProviders(dialogRef, config, container) : configProviders;

				return [...baseProviders, ...extraProviders];
			},
		};

		this.dialog.open<R, D, T>(componentOrTemplate, cdkConfig);

		if (!azmRef) {
			throw new Error("AzmDialogRef was not created for the opened dialog.");
		}

		keepToastOverlayElevated();
		elevateToastOverlay();

		return azmRef;
	}

	openResult<R, D = object>(
		component: ComponentType<object>,
		config: DialogConfig<D, CdkDialogRef<R, object>> = {},
	): AzmDialogRef<object, R> {
		return this.open<object, D, R>(component, config);
	}

	closeAll(): void {
		for (const cdkRef of [...this.dialog.openDialogs]) {
			closeCdkDialogWithAnimation(cdkRef);
		}
	}
}
